import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { brandConfig } from '../../../config/brands';

export type TabKey = 'stillImage' | 'scenario';
export type StillImageMode = 'attributes' | 'prompt' | 'purpose';
export type ScenarioMode = 'attributes' | 'prompt';

/**
 * /studio 配下の URL とタブ・モード state の双方向同期。
 *
 * GenerationWorkspace から切り出したもので、ロジックは移設のみ (挙動不変)。
 * 扱っている事情:
 * - 新URL構造 (/studio/image[/prompt|/purpose] / /studio/video[/prompt|/{id}]) の解決
 * - 旧URL (?tab= / ?mode= / ?scenario_id=) の互換
 * - /studio 裸アクセス時の localStorage 復元 + URL 合わせ
 * - HypeApp の既定モード (プロンプト入力優先) 分岐
 * - 目的軸モード (platform 限定) の URL 直叩きフォールバック
 * - pendingMode (編集リクエスト) によるタブ強制切替
 */
export function useStudioRouting({
  activeScenarioId,
  setActiveScenarioId,
  openMaterialsPanel,
  closeMaterialsPanel,
  isMobile,
  isPurposeModeAvailable,
  authUser,
  pendingMode,
}: {
  activeScenarioId: string | null;
  setActiveScenarioId: (id: string | null) => void;
  openMaterialsPanel: (mode: 'select' | 'manage') => void;
  closeMaterialsPanel: () => void;
  isMobile: boolean;
  isPurposeModeAvailable: boolean;
  /** JWT デコード結果。null (デコード前) は判定不能として purpose のフォールバックをしない */
  authUser: unknown;
  pendingMode: 'prompt' | 'video' | null;
}) {
  // URLパスとパラメータからtabとmodeを取得
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 旧URLパラメータ（互換性のため）
  const tabParam = searchParams.get('tab');
  const modeParam = searchParams.get('mode');
  const scenarioIdParam = searchParams.get('scenario_id');

  // URLパスからタブとモードを判定
  const getTabAndModeFromPath = (): { tab: TabKey; mode: StillImageMode } => {
    const path = location.pathname;

    // HypeApp ブランドのトップページ（/）は静止画プロンプト入力モードを既定とする
    if (path === '/' && brandConfig.homePageVariant === 'hypeapp') {
      return { tab: 'stillImage', mode: 'prompt' };
    }

    // 新URL構造: /studio/video/prompt → scenario タブ + prompt モード
    if (path === '/studio/video/prompt') {
      return { tab: 'scenario', mode: 'prompt' };
    }
    // 新URL構造: /studio/video/* → scenario タブ
    if (path.startsWith('/studio/video')) {
      return { tab: 'scenario', mode: 'attributes' };
    }
    // 新URL構造: /studio/image/prompt → stillImage タブ + prompt モード
    if (path === '/studio/image/prompt') {
      return { tab: 'stillImage', mode: 'prompt' };
    }
    // 新URL構造: /studio/image/purpose → stillImage タブ + 目的軸モード (計画書§6)
    if (path === '/studio/image/purpose') {
      return { tab: 'stillImage', mode: 'purpose' };
    }
    // 新URL構造: /studio/image → stillImage タブ
    if (path.startsWith('/studio/image')) {
      return { tab: 'stillImage', mode: 'attributes' };
    }
    // /studio 直接アクセス → localStorage から復元
    if (path === '/studio' || path === '/studio/') {
      const saved = localStorage.getItem('studio_active_tab');
      const isHypeapp = brandConfig.id === 'hypeapp';
      if (saved === 'scenario') {
        const savedScenarioMode = localStorage.getItem('studio_scenario_mode');
        const mode = (isHypeapp && savedScenarioMode !== 'attributes') ? 'prompt' : 'attributes';
        return { tab: 'scenario', mode };
      }
      const savedStillMode = localStorage.getItem('studio_still_image_mode');
      // 明示的に選択済みのモード (attributes/prompt/purpose のいずれか) はそのまま尊重する。
      // 未選択 (null 等) の場合のみブランド既定 (HypeApp はプロンプト優先) にフォールバックする。
      const stillMode: StillImageMode =
        savedStillMode === 'attributes' || savedStillMode === 'prompt' || savedStillMode === 'purpose'
          ? savedStillMode
          : (isHypeapp ? 'prompt' : 'attributes');
      return { tab: 'stillImage', mode: stillMode };
    }
    if (path.startsWith('/studio')) {
      return { tab: 'stillImage', mode: 'attributes' };
    }

    // 旧URL構造（互換性）: クエリパラメータから判定
    if (tabParam === 'scenario' || tabParam === 'video' || scenarioIdParam) {
      return { tab: 'scenario', mode: 'attributes' };
    }
    if (tabParam === 'stillImage') {
      const mode = (modeParam === 'prompt') ? 'prompt' : 'attributes';
      return { tab: 'stillImage', mode };
    }

    // デフォルト
    return { tab: 'stillImage', mode: 'attributes' };
  };

  const { tab: initialTab, mode: initialMode } = getTabAndModeFromPath();

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [stillImageMode, setStillImageMode] = useState<StillImageMode>(initialMode);
  const [scenarioMode, setScenarioMode] = useState<ScenarioMode>(
    initialTab === 'scenario' && initialMode !== 'purpose' ? initialMode : 'attributes',
  );

  // /studio 直接アクセスでlocalStorage復元した場合、URLを合わせる
  useEffect(() => {
    const path = location.pathname;
    if (path === '/studio' || path === '/studio/') {
      if (initialTab === 'scenario') {
        // HypeApp: ユーザーが明示的に属性選択を選んでいない限り、プロンプト入力モードを既定にする
        const savedScenarioMode = localStorage.getItem('studio_scenario_mode');
        const preferPrompt = brandConfig.id === 'hypeapp' && savedScenarioMode !== 'attributes';
        if (preferPrompt) {
          navigate('/studio/video/prompt', { replace: true });
        } else {
          const savedScenarioId = localStorage.getItem('studio_active_scenario_id');
          if (savedScenarioId) {
            setActiveScenarioId(savedScenarioId);
            navigate(`/studio/video/${savedScenarioId}`, { replace: true });
          } else {
            navigate('/studio/video', { replace: true });
          }
        }
      } else {
        navigate('/studio/image', { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URLパスが変更されたらタブとモードを更新 + localStorageにシナリオIDを保存
  //
  // 注意: activeScenarioId は「読む」だけで deps に含めない (ref 経由で idempotency 判定)。
  // この effect は URL の変更に反応して activeScenarioId を「書く」authority なので、
  // activeScenarioId を dep にすると、他の effect (例: work フェーズの既定選択
  // ScenarioGenerator の scenarios[0].id セット) が activeScenarioId を書いた瞬間に
  // 本 effect が再発火し、URL 由来の値へ上書き → 相手が再度書く…の無限ピンポンになり、
  // currentScope (activeScenarioId ?? STILL_IMAGE_SCOPE) が振動して selectedAssets が
  // 0↔N でちらつく。URL 変更時のみ発火させることでこのループを断つ。
  const activeScenarioIdRef = useRef(activeScenarioId);
  activeScenarioIdRef.current = activeScenarioId;
  useEffect(() => {
    const { tab, mode } = getTabAndModeFromPath();
    setActiveTab(tab);
    if (tab === 'stillImage') {
      setStillImageMode(mode);
    } else if (tab === 'scenario' && mode !== 'purpose') {
      // 目的軸モードは静止画タブ専用 (動画タブには存在しない)
      setScenarioMode(mode);
    }
    // /studio/video/{id} のシナリオIDをlocalStorageに保存（/studio/video/prompt は除外）
    const path = location.pathname;
    const videoMatch = path.match(/^\/studio\/video\/(?!prompt$)(.+)$/);
    if (videoMatch) {
      if (activeScenarioIdRef.current !== videoMatch[1]) {
        setActiveScenarioId(videoMatch[1]);
      }
      localStorage.setItem('studio_active_tab', 'scenario');
      localStorage.setItem('studio_active_scenario_id', videoMatch[1]);
    } else if (path.startsWith('/studio/video')) {
      if (activeScenarioIdRef.current !== null) {
        setActiveScenarioId(null);
      }
      localStorage.setItem('studio_active_tab', 'scenario');
      localStorage.removeItem('studio_active_scenario_id');
    } else if (path.startsWith('/studio/image')) {
      if (activeScenarioIdRef.current !== null) {
        setActiveScenarioId(null);
      }
      localStorage.setItem('studio_active_tab', 'stillImage');
      localStorage.removeItem('studio_active_scenario_id');
    }
  // activeScenarioId は意図的に dep から除外 (上のコメント参照)。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tabParam, modeParam, scenarioIdParam, setActiveScenarioId]);

  // HypeApp: /studio/image / /studio/video への裸アクセス時、localStorage で明示的に
  // 属性選択が選ばれていなければプロンプトモードを既定にする
  useEffect(() => {
    if (brandConfig.id !== 'hypeapp') return;
    const path = location.pathname;
    if (path === '/studio/image') {
      const saved = localStorage.getItem('studio_still_image_mode');
      if (saved === 'purpose') {
        navigate('/studio/image/purpose', { replace: true });
      } else if (saved !== 'attributes') {
        navigate('/studio/image/prompt', { replace: true });
      }
    } else if (path === '/studio/video') {
      const saved = localStorage.getItem('studio_scenario_mode');
      if (saved !== 'attributes') {
        navigate('/studio/video/prompt', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // タブ切り替え時のナビゲーション
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    localStorage.setItem('studio_active_tab', tab);
    localStorage.removeItem('studio_active_scenario_id');
    // 動画生成タブに切り替え時は素材パネルを閉じる
    if (tab === 'scenario') {
      closeMaterialsPanel();
      // HypeApp: ユーザーが明示的に属性選択を選んでいない限り、プロンプト入力モードを既定にする
      const savedScenarioMode = localStorage.getItem('studio_scenario_mode');
      const preferPrompt = brandConfig.id === 'hypeapp' && savedScenarioMode !== 'attributes';
      if (preferPrompt) {
        // ScenarioGenerator は initialMode を mount 時のみ参照するため、先に state を更新しておく
        setScenarioMode('prompt');
        navigate('/studio/video/prompt');
      } else if (activeScenarioId) {
        setScenarioMode('attributes');
        localStorage.setItem('studio_active_scenario_id', activeScenarioId);
        navigate(`/studio/video/${activeScenarioId}`);
      } else {
        setScenarioMode('attributes');
        navigate('/studio/video');
      }
    } else {
      setActiveScenarioId(null);
      const savedMode = localStorage.getItem('studio_still_image_mode');
      if (savedMode === 'prompt') {
        navigate('/studio/image/prompt');
      } else {
        navigate('/studio/image');
      }
    }
  };

  // モード切り替え時のナビゲーション（静止画タブ内）
  const handleModeChange = (mode: StillImageMode) => {
    setStillImageMode(mode);
    localStorage.setItem('studio_still_image_mode', mode);
    if (mode === 'prompt') {
      navigate('/studio/image/prompt');
    } else if (mode === 'purpose') {
      navigate('/studio/image/purpose');
    } else {
      navigate('/studio/image');
    }
  };

  // 目的軸モードは platform 限定 (isPurposeModeAvailable)。非対象ユーザーが URL 直叩き・
  // localStorage 復元で purpose に入った場合は属性モードへ落とす。JWT デコード前
  // (authUser=null) は判定できないため何もしない (誤フォールバック防止)。
  useEffect(() => {
    if (stillImageMode !== 'purpose') return;
    if (!authUser || isPurposeModeAvailable) return;
    setStillImageMode('attributes');
    localStorage.setItem('studio_still_image_mode', 'attributes');
    navigate('/studio/image', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stillImageMode, authUser, isPurposeModeAvailable]);

  // モード切り替え時のナビゲーション（動画タブ内）
  const handleScenarioModeChange = (mode: ScenarioMode) => {
    setScenarioMode(mode);
    localStorage.setItem('studio_scenario_mode', mode);
    if (mode === 'prompt') {
      if (!isMobile) {
        openMaterialsPanel('manage');
      }
      navigate('/studio/video/prompt');
    } else {
      closeMaterialsPanel();
      navigate('/studio/video');
    }
  };

  // 編集リクエスト (素材の「AIに修正依頼」/ トップページのプロンプト) があれば、静止画のプロンプト入力モードに切り替え。
  // handleModeChange と同じく URL と localStorage も合わせる (state だけ変えると URL が /studio/image のままで、
  // リロードで属性モードへ戻っていた)。pendingMode のクリアは pendingPrompt を受け取る GenerationPreviewPanel が
  // 行う (空文字でもクリアする)。クリアされないと次の setPendingEdit が同値更新になり、この effect が再実行されない
  useEffect(() => {
    if (pendingMode === 'prompt') {
      setActiveTab('stillImage');
      setStillImageMode('prompt');
      localStorage.setItem('studio_still_image_mode', 'prompt');
      if (location.pathname !== '/studio/image/prompt') {
        navigate('/studio/image/prompt');
      }
      closeMaterialsPanel(); // スマホで素材パネルを閉じてプロンプトフォームを表示
    } else if (pendingMode === 'video') {
      setActiveTab('scenario');
      // ScenarioGeneratorでclearPendingEditを呼ぶので、ここではクリアしない
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMode]);

  return {
    location,
    navigate,
    activeTab,
    stillImageMode,
    scenarioMode,
    handleTabChange,
    handleModeChange,
    handleScenarioModeChange,
  };
}
